/**
 * PE-2084 dk-4 — the click handler a merchant pastes onto their own Donate button.
 *
 * The embed bundle is `defer`red, so on a slow connection a merchant's button can
 * be tapped seconds before it runs. Until then `window.CharityStack` does not
 * exist, and the handler this generator used to emit —
 * `CharityStack.openOverlay(...)` — threw ReferenceError and the tap was lost.
 *
 * The handler now does one of two things:
 *  - the API exists → call `CharityStack.openOverlay(...)`, exactly as before;
 *  - it does not yet → push `['openOverlay', [args], Date.now()]` onto
 *    `window.CharityStack.q`. The bundle's `installPublicApi()`
 *    (donation-elements-backend `hosted-scripts/charitystack-base.js`) drains
 *    that queue when it runs: one open for repeated taps (the latest), entries
 *    older than 15 s dropped, then `q` deleted.
 *
 * FROZEN (`D-embed-snippet-frozen`): the embed `<script>` tag and the
 * `openOverlay(formID, type?, elementID?)` signature. Only the per-button snippet
 * changes, and only for NEW copies — snippets already pasted keep working
 * exactly as they did. The `q` name and entry shape are a contract with the
 * bundle once a merchant has pasted one; do not change them.
 *
 * `args` are the RAW argument values (ids, type). Each becomes a JS string
 * literal via `JSON.stringify` — re-quoted with single quotes, so an ordinary id
 * produces exactly the `'form-1'` the snippet has always shown — and the HTML
 * form is then attribute-escaped (`&`, `"`, `<`, `>`), so a value can neither end
 * the `onclick="…"` attribute nor open a tag.
 */
export function jsStringLiteral(value: string): string {
  const body = JSON.stringify(String(value))
    .slice(1, -1)
    .replace(/\\"/g, '"')
    .replace(/'/g, "\\'");
  return `'${body}'`;
}

export function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * The handler as plain JavaScript source — what to hand to anything that writes
 * JS (the Prompt tab). NOT attribute-escaped: `&quot;` in JS is a syntax error.
 */
export function overlayButtonJsHandler(args: string[]): string {
  const list = args.map(jsStringLiteral).join(', ');
  return (
    `(window.CharityStack=window.CharityStack||{}).openOverlay` +
    `?CharityStack.openOverlay(${list})` +
    `:(CharityStack.q=CharityStack.q||[]).push(['openOverlay',[${list}],Date.now()])`
  );
}

/** The same handler, attribute-escaped, for inside `onclick="…"` only. */
export function overlayButtonHtmlOnclick(args: string[]): string {
  return escapeHtmlAttribute(overlayButtonJsHandler(args));
}

/** The same handler as a React `onClick` arrow function (JSX source text). */
export function overlayButtonReactOnClick(args: string[]): string {
  const list = args.map(jsStringLiteral).join(', ');
  return (
    `() => { const cs = (window.CharityStack = window.CharityStack || {}); ` +
    `cs.openOverlay ? cs.openOverlay(${list}) : (cs.q = cs.q || []).push(['openOverlay', [${list}], Date.now()]); }`
  );
}
